import { useSearchParams } from "react-router-dom";
import { Mail, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const MockRecommendationEmail = () => {
  const [searchParams] = useSearchParams();

  const recommenderName = searchParams.get("recommenderName") || "Name Lastname";
  const applicantName = searchParams.get("applicantName") || "Student Name";
  const program = searchParams.get("program") || "the program";
  const portalToken = searchParams.get("portalToken") || "";
  const advisorName = searchParams.get("advisorName") || "Admissions Office";
  const advisorEmail = searchParams.get("advisorEmail") || "admissions@usa.edu";
  const advisorTitle = searchParams.get("advisorTitle") || "Enrollment Advisor";
  const recommenderEmail = searchParams.get("recommenderEmail") || "";

  const portalUrl = `${window.location.origin}/recommend/${portalToken}`;

  const handleOpenPortal = () => {
    window.open(portalUrl, "_blank");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 dark:from-slate-900 dark:to-slate-950">
      {/* Email Client Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded bg-blue-500 flex items-center justify-center">
              <Mail className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-slate-700 dark:text-slate-200">Mock Email Client</span>
            <span className="text-xs bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-400 px-2 py-1 rounded-full">
              Development Mode
            </span>
          </div>
          <span className="text-sm text-slate-500">Inbox</span>
        </div>
      </div>

      {/* Email Content */}
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden">
          {/* Email Header */}
          <div className="border-b border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#002855] flex items-center justify-center">
                  <span className="text-white font-bold text-lg">USA</span>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 dark:text-white">
                    University of St. Augustine for Health Sciences
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {advisorEmail}
                  </p>
                </div>
              </div>
              <span className="text-sm text-slate-400">Just now</span>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                <span className="text-slate-400">To:</span> {recommenderEmail || "recommender@example.com"}
              </p>
              <h2 className="text-xl font-semibold text-slate-800 dark:text-white">
                Recommendation Request for {applicantName}
              </h2>
            </div>
          </div>

          {/* Email Body */}
          <div className="p-8">
            {/* Logo */}
            <div className="text-center mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_student-journey-17/artifacts/lcfdritp_primary.Horiz.2line.knockedout.pms315.png"
                alt="University of St. Augustine for Health Sciences"
                className="h-16 mx-auto"
              />
            </div>

            {/* Divider line matching the email template */}
            <div className="h-[3px] bg-gradient-to-r from-[#00677F] via-[#C8102E] to-[#C8102E] mb-8 max-w-lg mx-auto rounded-full" />

            {/* Content */}
            <div className="max-w-lg mx-auto">
              <p className="text-slate-700 dark:text-slate-200 mb-4">
                Dear {recommenderName},
              </p>

              <p className="text-slate-600 dark:text-slate-300 mb-4">
                <strong className="text-slate-800 dark:text-white">{applicantName}</strong> is applying to the{" "}
                <strong className="text-slate-800 dark:text-white">{program}</strong> program at the University of St. Augustine.
              </p>

              <p className="text-slate-600 dark:text-slate-300 mb-4">
                You have been identified as someone who can speak to the applicant's ability to be successful in this rigorous program, 
                and are being asked to complete a short reference form.
              </p>

              <p className="text-slate-600 dark:text-slate-300 mb-6">
                If you're willing, please click the link below as soon as possible to get started, 
                as it is a critical piece of <strong className="text-slate-800 dark:text-white">{applicantName}'s</strong> application.
              </p>

              {/* CTA Button */}
              <div className="text-center mb-6">
                <Button
                  onClick={handleOpenPortal}
                  className="px-8 py-6 bg-[#00677F] hover:bg-[#135163] text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                  data-testid="open-rec-portal-btn"
                >
                  <span className="flex items-center gap-2">
                    <ExternalLink className="w-5 h-5" />
                    Access Recommendation Form
                  </span>
                </Button>
              </div>

              {/* Link fallback */}
              <div className="bg-slate-100 dark:bg-slate-900 rounded-lg p-3 break-all mb-8">
                <p className="text-slate-500 dark:text-slate-400 text-xs mb-1">
                  Or copy and paste this link:
                </p>
                <code className="text-xs text-[#00677F]">{portalUrl}</code>
              </div>

              <p className="text-slate-600 dark:text-slate-300 mb-6">
                If you have any questions, please contact me.
              </p>

              {/* Signature */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                <p className="text-slate-600 dark:text-slate-300 mb-1">Sincerely,</p>
                <p className="text-slate-800 dark:text-white font-semibold">{advisorName}</p>
                <p className="text-slate-600 dark:text-slate-300 text-sm">{advisorTitle}</p>
                <p className="text-slate-600 dark:text-slate-300 text-sm">University of St. Augustine</p>
                <a href={`mailto:${advisorEmail}`} className="text-[#00677F] text-sm hover:underline">{advisorEmail}</a>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-slate-200 dark:border-slate-700 mt-8 pt-8 text-center">
              <p className="text-slate-400 dark:text-slate-500 text-xs">
                © 2026 University of St. Augustine for Health Sciences. All rights reserved.
              </p>
              <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">
                San Marcos, CA / St. Augustine, FL / Austin, TX
              </p>
            </div>
          </div>
        </div>

        {/* Development Notice */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-4 py-2 rounded-full text-sm">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            This is a mock email for development. In production, this would be sent via email service.
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockRecommendationEmail;
